import { Checkbox, FormInput, Hint, Modal, PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { updateActiveCampaignSettings } from 'modules/profile/integrations/active-campaign-integration/api/active-campaign-settings-api'
import {
  ActiveCampaignTriggerEnum,
  ActiveCampaignTriggerTitle,
} from 'modules/profile/integrations/active-campaign-integration/enums/active-campaign-trigger-enum'
import { useActiveCampaignSettings } from 'modules/profile/integrations/active-campaign-integration/hooks/use-active-campaign-settings'
import {
  ActiveCampaignSettingsErrorInterface,
  ActiveCampaignSettingsInterface,
} from 'modules/profile/integrations/active-campaign-integration/types/active-campaign-settings-interface'
import { WrapperIntegration } from 'modules/profile/integrations/components/wrapper-integration'
import { WrapperIntegrationSkeleton } from 'modules/profile/integrations/components/wrapper-integtation-skeleton'
import { getHelpPageLinkActiveCampaign } from 'modules/profile/integrations/utils/get-help-page-link-active-campaign'
import React, { useEffect, useState } from 'react'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { ActiveCampaignIntegrationIcon } from '@systemeio/ui/icons'

const defaultValues: ActiveCampaignSettingsInterface = {
  active: false,
  apiUrl: null,
  token: null,
  triggers: [],
}

const defaultErrors = {
  apiUrl: '',
  token: '',
  common: [],
}

export const ActiveCampaignIntegrationComingSoon = () => {
  const { t } = useLocoTranslation()
  return (
    <WrapperIntegration
      icon={<ActiveCampaignIntegrationIcon />}
      title={t('dashboard.user_settings.integrations_page.active_campaign.name')}
      subTitle={t('dashboard.user_settings.integrations_page.active_campaign.subtitle-2')}
      isComingSoon
    />
  )
}

export const ActiveCampaignIntegration = () => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const [tempState, setTempState] = useState<ActiveCampaignSettingsInterface>(defaultValues)
  const [opened, setOpened] = useState(false)

  const [errors, setErrors] = useState<ActiveCampaignSettingsErrorInterface>(defaultErrors)

  const [isFetching, setIsFetching] = useState(false)

  const { activeCampaignData, mutate } = useActiveCampaignSettings()

  function handleOpenModal() {
    setOpened(true)
  }

  function handleCloseModal() {
    setOpened(false)
  }

  function handleAfterLeaveModal() {
    setTempState(defaultValues)
  }

  const onSubmit = async () => {
    try {
      setErrors(defaultErrors)
      setIsFetching(true)
      const response = await updateActiveCampaignSettings(tempState)
      await mutate(response.data, false)
      setIsFetching(false)
      toast.success(t('global.changes_saved'))
      setErrors(defaultErrors)
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const activeCampaignSettingErrors = e.errors
        setErrors(prev => ({
          ...prev,
          apiUrl: activeCampaignSettingErrors.fields.apiUrl?.join(''),
          token: activeCampaignSettingErrors.fields.token?.join(''),
        }))
        if (e.errors.common) {
          const commonErrors = e.errors.common
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors,
            }
          })
        }
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  useEffect(() => {
    if (activeCampaignData) {
      setTempState({
        ...activeCampaignData,
      })
    }
  }, [activeCampaignData, opened])

  return (
    <>
      {activeCampaignData ? (
        <>
          <WrapperIntegration
            icon={<ActiveCampaignIntegrationIcon />}
            title={t('dashboard.user_settings.integrations_page.active_campaign.name')}
            subTitle={t('dashboard.user_settings.integrations_page.active_campaign.subtitle-2')}
            onClick={handleOpenModal}
            isConnect={activeCampaignData.active}
          />
          <Modal
            title={t('dashboard.user_settings.integrations_page.active_campaign.title')}
            onClose={handleCloseModal}
            opened={opened}
            isFetching={isFetching}
            afterLeave={handleAfterLeaveModal}
          >
            <>
              <div className={'mb-2.5 lg:mb-7'}>
                <span>
                  <TransLoco
                    t={t}
                    i18nKey={'dashboard.user_settings.integrations_page.active_campaign.help'}
                    components={{
                      a: (
                        <a
                          href={getHelpPageLinkActiveCampaign(user?.dashboardLocale)}
                          target="_blank"
                          rel="noreferrer"
                          className={'primary-link'}
                        />
                      ),
                      b: <b />,
                    }}
                  />
                </span>
              </div>
              <div className="mb-3 flex flex-col gap-3.5">
                <Checkbox
                  value={tempState.active}
                  label={t('global.activate')}
                  onChange={() =>
                    setTempState(prev => {
                      return { ...prev, active: !prev.active }
                    })
                  }
                />
                <FormInput
                  label={t(
                    'dashboard.user_settings.integrations_page.active_campaign.api_url_title',
                  )}
                  required
                  value={tempState.apiUrl || ''}
                  onChange={e => {
                    setTempState(prev => ({
                      ...prev,
                      apiUrl: e.target.value,
                    }))
                  }}
                  error={errors.apiUrl}
                />
                <FormInput
                  label={
                    <Hint
                      label={t(
                        'dashboard.user_settings.integrations_page.active_campaign.api_key_popover_title-2',
                      )}
                    >
                      {t('dashboard.user_settings.integrations_page.active_campaign.api_key_title')}
                    </Hint>
                  }
                  required
                  value={tempState.token || ''}
                  onChange={e =>
                    setTempState(prev => ({
                      ...prev,
                      token: e.target.value,
                    }))
                  }
                  error={errors.token}
                />
              </div>

              <div className="mb-2.5 flex flex-col gap-2 lg:mb-7">
                <Hint
                  label={t(
                    'dashboard.user_settings.integrations_page.active_campaign.triggers_popover_title-2',
                  )}
                  className={'mb-2.5'}
                >
                  {t('dashboard.user_settings.integrations_page.active_campaign.triggers_title')}
                </Hint>
                <div className="flex flex-col gap-4">
                  {Object.entries(ActiveCampaignTriggerTitle).map(([key, title]) => {
                    const isSelected = tempState.triggers.includes(key as ActiveCampaignTriggerEnum)
                    const label = t(title)
                    return (
                      <div key={key}>
                        <Checkbox
                          value={isSelected}
                          label={
                            <div
                              className={`flex items-center gap-1.5 ${
                                isSelected &&
                                '[&>svg]:fill-brand-primary [&>svg]:main-transition-colors'
                              }`}
                            >
                              {label}
                            </div>
                          }
                          onChange={() =>
                            isSelected
                              ? setTempState(prev => {
                                  return {
                                    ...prev,
                                    triggers: prev.triggers.filter(method => method !== key),
                                  }
                                })
                              : setTempState(prev => {
                                  return {
                                    ...prev,
                                    triggers: [...prev.triggers, key as ActiveCampaignTriggerEnum],
                                  }
                                })
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className={'flex justify-center'}>
                <PrimaryButton onClick={onSubmit} isFetching={isFetching}>
                  {t('global.save')}
                </PrimaryButton>
              </div>
              {errors.common && (
                <>
                  {errors.common.map((error, index) => (
                    <p className="text-danger text-sm" key={index}>
                      {error}
                    </p>
                  ))}
                </>
              )}
            </>
          </Modal>
        </>
      ) : (
        <WrapperIntegrationSkeleton />
      )}
    </>
  )
}
